import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  LinearProgress,
  Container,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Map as MapIcon,
  Info as InfoIcon,
  FileUpload as FileUploadIcon,
  Layers as LayerIcon,
  Visibility as VisibilityIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useAuth } from '../../hooks/auth/useAuth';
import { organizationLayersApi } from '../../services/api';

const GeoJsonManagementPage = () => {
  const { user } = useAuth();
  
  // State management
  const [layers, setLayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Upload dialog state
  const [uploadDialog, setUploadDialog] = useState({
    open: false,
    layerName: '',
    selectedFile: null,
    uploading: false,
    uploadProgress: 0
  });
  
  // Edit dialog state
  const [editDialog, setEditDialog] = useState({
    open: false,
    layer: null,
    newName: ''
  });
  
  // Delete confirmation dialog
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    layer: null,
    deleting: false
  });

  // Fetch layers on component mount
  useEffect(() => {
    fetchLayers();
  }, [user?.organizationId]);

  const fetchLayers = async () => {
    if (!user?.organizationId) {
      setError('No organization ID found');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await organizationLayersApi.getLayers(user.organizationId);
      setLayers(response.data || []);
    } catch (err) {
      console.error('Error fetching GeoJSON layers:', err);
      setError('Failed to load GeoJSON layers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadDialogOpen = () => {
    setUploadDialog({
      open: true,
      layerName: '',
      selectedFile: null,
      uploading: false,
      uploadProgress: 0
    });
  };

  const handleUploadDialogClose = () => {
    if (!uploadDialog.uploading) {
      setUploadDialog({
        open: false,
        layerName: '',
        selectedFile: null,
        uploading: false,
        uploadProgress: 0
      });
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.name.toLowerCase().endsWith('.geojson')) {
        setError('Please select a valid GeoJSON file (.geojson extension)');
        return;
      }
      
      // Validate file size (50MB limit)
      if (file.size > 50 * 1024 * 1024) {
        setError('File size must be less than 50MB');
        return;
      }
      
      setUploadDialog(prev => ({
        ...prev,
        selectedFile: file,
        layerName: prev.layerName || file.name.replace('.geojson', '').replace(/[^a-zA-Z0-9\s]/g, ' ').trim()
      }));
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!uploadDialog.selectedFile || !uploadDialog.layerName.trim()) {
      setError('Please select a file and enter a layer name');
      return;
    }

    try {
      setUploadDialog(prev => ({ ...prev, uploading: true, uploadProgress: 0 }));
      setError(null);

      const formData = new FormData();
      formData.append('geojson_file', uploadDialog.selectedFile);
      formData.append('layer_name', uploadDialog.layerName.trim());

      // Upload with progress tracking
      const response = await organizationLayersApi.createLayer(user.organizationId, formData);
      
      setSuccess(`Layer "${uploadDialog.layerName}" uploaded successfully!`);
      handleUploadDialogClose();
      
      // Refresh layers list
      await fetchLayers();
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
      
    } catch (err) {
      console.error('Error uploading GeoJSON layer:', err);
      setError(err.response?.data?.error || 'Failed to upload layer. Please try again.');
    } finally {
      setUploadDialog(prev => ({ ...prev, uploading: false, uploadProgress: 0 }));
    }
  };

  const handleEditDialogOpen = (layer) => {
    setEditDialog({
      open: true,
      layer,
      newName: layer.layer_name
    });
  };

  const handleEditDialogClose = () => {
    setEditDialog({
      open: false,
      layer: null,
      newName: ''
    });
  };

  const handleEditSave = async () => {
    if (!editDialog.newName.trim()) {
      setError('Layer name cannot be empty');
      return;
    }

    try {
      setError(null);
      
      await organizationLayersApi.updateLayer(
        user.organizationId,
        editDialog.layer.id,
        { layer_name: editDialog.newName.trim() }
      );
      
      setSuccess(`Layer renamed to "${editDialog.newName}" successfully!`);
      handleEditDialogClose();
      
      // Refresh layers list
      await fetchLayers();
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
      
    } catch (err) {
      console.error('Error updating layer:', err);
      setError(err.response?.data?.error || 'Failed to update layer. Please try again.');
    }
  };

  const handleDeleteDialogOpen = (layer) => {
    setDeleteDialog({
      open: true,
      layer,
      deleting: false
    });
  };

  const handleDeleteDialogClose = () => {
    if (!deleteDialog.deleting) {
      setDeleteDialog({
        open: false,
        layer: null,
        deleting: false
      });
    }
  };

  const handleDelete = async () => {
    try {
      setDeleteDialog(prev => ({ ...prev, deleting: true }));
      setError(null);
      
      await organizationLayersApi.deleteLayer(user.organizationId, deleteDialog.layer.id);
      
      setSuccess(`Layer "${deleteDialog.layer.layer_name}" deleted successfully!`);
      handleDeleteDialogClose();
      
      // Refresh layers list
      await fetchLayers();
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
      
    } catch (err) {
      console.error('Error deleting layer:', err);
      setError(err.response?.data?.error || 'Failed to delete layer. Please try again.');
    } finally {
      setDeleteDialog(prev => ({ ...prev, deleting: false }));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MapIcon color="primary" />
          Map Layers Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Upload and manage GeoJSON layers for your organization's map visualization. 
          These layers will be displayed on the map view for all users in your organization.
        </Typography>
      </Box>

      {/* Alert messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Upload Card */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UploadIcon color="primary" />
            Upload New Layer
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Upload a GeoJSON file to add a new map layer. Supported format: .geojson (max 50MB)
          </Typography>
          <Button
            variant="contained"
            startIcon={<FileUploadIcon />}
            onClick={handleUploadDialogOpen}
            size="large"
          >
            Upload GeoJSON Layer
          </Button>
        </CardContent>
      </Card>

      {/* Layers List */}
      <Card>
        <CardContent>
          <Typography variant="h6" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LayerIcon color="primary" />
            Your Organization's Layers ({layers.length})
          </Typography>
          
          {layers.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <MapIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No layers uploaded yet
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Upload your first GeoJSON layer to get started with custom map visualization.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={handleUploadDialogOpen}
              >
                Upload Your First Layer
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Layer Name</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Created By</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {layers.map((layer) => (
                    <TableRow key={layer.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LayerIcon color="primary" fontSize="small" />
                          <Typography variant="body2" fontWeight="medium">
                            {layer.layer_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(layer.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {layer.created_by_email || 'Unknown'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<CheckIcon />}
                          label="Active"
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="Edit layer name">
                            <IconButton
                              size="small"
                              onClick={() => handleEditDialogOpen(layer)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete layer">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteDialogOpen(layer)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog 
        open={uploadDialog.open} 
        onClose={handleUploadDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload GeoJSON Layer</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Layer Name"
              value={uploadDialog.layerName}
              onChange={(e) => setUploadDialog(prev => ({ ...prev, layerName: e.target.value }))}
              fullWidth
              disabled={uploadDialog.uploading}
              helperText="Enter a descriptive name for this layer"
            />
            
            <Box>
              <input
                accept=".geojson"
                style={{ display: 'none' }}
                id="geojson-file-input"
                type="file"
                onChange={handleFileSelect}
                disabled={uploadDialog.uploading}
              />
              <label htmlFor="geojson-file-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<FileUploadIcon />}
                  fullWidth
                  sx={{ py: 2 }}
                  disabled={uploadDialog.uploading}
                >
                  {uploadDialog.selectedFile ? uploadDialog.selectedFile.name : 'Select GeoJSON File'}
                </Button>
              </label>
              {uploadDialog.selectedFile && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  File size: {formatFileSize(uploadDialog.selectedFile.size)}
                </Typography>
              )}
            </Box>
            
            {uploadDialog.uploading && (
              <Box>
                <LinearProgress />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Uploading layer...
                </Typography>
              </Box>
            )}
            
            <Alert severity="info" icon={<InfoIcon />}>
              <Typography variant="body2">
                <strong>File Requirements:</strong>
                <br />• Valid GeoJSON format with .geojson extension
                <br />• Maximum file size: 50MB
                <br />• Must contain a FeatureCollection
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleUploadDialogClose}
            disabled={uploadDialog.uploading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpload}
            variant="contained"
            disabled={!uploadDialog.selectedFile || !uploadDialog.layerName.trim() || uploadDialog.uploading}
            startIcon={uploadDialog.uploading ? <CircularProgress size={16} /> : <UploadIcon />}
          >
            {uploadDialog.uploading ? 'Uploading...' : 'Upload Layer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog 
        open={editDialog.open} 
        onClose={handleEditDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Layer Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Layer Name"
            value={editDialog.newName}
            onChange={(e) => setEditDialog(prev => ({ ...prev, newName: e.target.value }))}
            fullWidth
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditDialogClose}>Cancel</Button>
          <Button 
            onClick={handleEditSave}
            variant="contained"
            disabled={!editDialog.newName.trim()}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialog.open} 
        onClose={handleDeleteDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Layer</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Are you sure you want to delete the layer "{deleteDialog.layer?.layer_name}"?
            </Typography>
          </Alert>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone. The layer will be permanently removed from your organization's map view.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDeleteDialogClose}
            disabled={deleteDialog.deleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={deleteDialog.deleting}
            startIcon={deleteDialog.deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleteDialog.deleting ? 'Deleting...' : 'Delete Layer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GeoJsonManagementPage; 